import { type MetaFunction } from "@remix-run/node";
import { useState } from "react";
import { Icon } from "#app/ui/components/custom/icon.js";
import { Container } from "#app/ui/components/verveui/layout/container.js";

export const meta: MetaFunction = () => {
  return [
    { title: "Prepara-te Para a Primeira Sessão — Estuda Comigo" },
    {
      name: "description",
      content:
        "Guia de preparação para a tua primeira sessão de explicações de programação.",
    },
  ];
};

const extensionsCommand =
  "code --install-extension wesbos.theme-cobalt2 && code --install-extension vscode-icons-team.vscode-icons && code --install-extension miguelsolorio.fluent-icons && code --install-extension tomoki1207.pdf";

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex shrink-0 items-center gap-1 rounded-md bg-muted-200 px-2 py-1 text-xs font-medium text-muted-600 transition-colors hover:bg-muted-300 dark:bg-muted-700 dark:text-muted-400 dark:hover:bg-muted-600"
    >
      <Icon name={copied ? "check" : "copy"} className="h-3.5 w-3.5" />
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
};

const CommandBlock = ({
  label,
  command,
}: {
  label: string;
  command: string;
}) => (
  <div className="rounded-lg border border-muted-200 dark:border-muted-800">
    <div className="flex items-center justify-between border-b border-muted-200 px-3 py-1.5 dark:border-muted-800">
      <span className="text-xs font-medium text-muted-500">{label}</span>
      <CopyButton text={command} />
    </div>
    <pre className="overflow-x-auto whitespace-pre-wrap break-all p-3 font-mono text-xs leading-relaxed text-muted-700 dark:text-muted-300">
      {command}
    </pre>
  </div>
);

const FolderStructure = () => {
  const [subjectName, setSubjectName] = useState("");
  const name = subjectName.trim() || "Nome da Cadeira";

  const macCommand = `mkdir -p "${name}"/{Exames,Testes,Teoria,Prática,Info} && touch "${name}/Plano de estudo.md"`;
  const windowsCommand = `"Exames","Testes","Teoria","Prática","Info" | ForEach-Object { New-Item -Path "${name}\\$_" -ItemType Directory -Force }; New-Item -Path "${name}\\Plano de estudo.md" -ItemType File -Force`;

  return (
    <div className="mt-3 space-y-3">
      <input
        type="text"
        value={subjectName}
        onChange={(e) => setSubjectName(e.target.value)}
        placeholder="Nome da cadeira (ex: Programação)"
        className="w-full rounded-lg border border-muted-200 bg-white px-3 py-2 text-sm text-muted-900 placeholder-muted-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand dark:border-muted-800 dark:bg-muted-950 dark:text-muted-100"
      />

      <pre className="overflow-x-auto rounded-lg bg-muted-50 px-4 py-3 font-mono text-sm leading-relaxed text-muted-700 dark:bg-muted-900 dark:text-muted-300">{`${name}/
├── Exames/
├── Testes/
├── Teoria/
├── Prática/
├── Info/
└── Plano de estudo.md`}</pre>

      <details className="group">
        <summary className="cursor-pointer text-sm font-medium text-brand hover:underline">
          Criar automaticamente no terminal
        </summary>
        <div className="mt-2 space-y-2">
          <CommandBlock label="Mac / Linux" command={macCommand} />
          <CommandBlock label="Windows (PowerShell)" command={windowsCommand} />
        </div>
      </details>
    </div>
  );
};

const StepCard = ({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex gap-3">
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-white"
      style={{ width: 28, height: 28, marginTop: 2 }}
    >
      {number}
    </div>
    <div className="min-w-0 flex-1">
      <h2 className="font-semibold text-muted-900">{title}</h2>
      {children}
    </div>
  </div>
);

const Route = () => {
  return (
    <Container className="py-12 sm:py-20">
      <div className="mx-auto max-w-xl">
        <div className="text-center">
          <p className="text-sm font-medium text-brand">Bem-vindo/a</p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-muted-900 sm:text-3xl">
            Prepara-te para a primeira sessão
          </h1>
          <p className="mt-3 text-muted-500">
            6 passos simples. Demora cerca de 10 minutos.
          </p>
        </div>

        <div className="mt-10 divide-y divide-muted-100 rounded-2xl border border-muted-200 bg-white p-6 shadow-sm dark:divide-muted-800 dark:border-muted-800 dark:bg-muted-950 sm:p-8">
          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={1} title="Instala o Zoom">
              <p className="mt-1 text-sm text-muted-500">
                Para as sessões por vídeo. Não precisas de criar conta.
              </p>
              <a
                href="https://zoom.us/download"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                Descarregar Zoom
                <Icon name="arrow-right" className="h-3.5 w-3.5" />
              </a>
            </StepCard>
          </div>

          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={2} title="Instala o VS Code">
              <p className="mt-1 text-sm text-muted-500">
                O editor de código que vamos usar. Gratuito, Windows/Mac/Linux.
              </p>
              <a
                href="https://code.visualstudio.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                Descarregar VS Code
                <Icon name="arrow-right" className="h-3.5 w-3.5" />
              </a>
            </StepCard>
          </div>

          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={3} title="Configura o VS Code — Settings">
              <p className="mt-1 text-sm text-muted-500">
                Abre o VS Code, prime{" "}
                <kbd className="rounded bg-muted-100 px-1 py-0.5 text-xs dark:bg-muted-800">
                  Ctrl+Shift+P
                </kbd>{" "}
                (Mac:{" "}
                <kbd className="rounded bg-muted-100 px-1 py-0.5 text-xs dark:bg-muted-800">
                  Cmd+Shift+P
                </kbd>
                ), escreve &quot;Open User Settings (JSON)&quot; e substitui o
                conteúdo pelo ficheiro abaixo.
              </p>
              <a
                href="https://github.com/andrecasal/VS-Code-Settings/blob/master/settings.json"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                Ver settings.json no GitHub
                <Icon name="arrow-right" className="h-3.5 w-3.5" />
              </a>
            </StepCard>
          </div>

          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={4} title="Configura o VS Code — Extensões">
              <p className="mt-1 text-sm text-muted-500">
                Corre este comando no terminal:
              </p>
              <div className="mt-2">
                <CommandBlock label="Terminal" command={extensionsCommand} />
              </div>
            </StepCard>
          </div>

          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={5} title="Cria a estrutura de pastas">
              <p className="mt-1 text-sm text-muted-500">
                Organiza os materiais da cadeira nesta estrutura:
              </p>
              <FolderStructure />
            </StepCard>
          </div>

          <div className="py-5 first:pt-0 last:pb-0">
            <StepCard number={6} title="Descarrega os materiais da cadeira">
              <p className="mt-1 text-sm text-muted-500">
                Vai ao Moodle/Blackboard da tua universidade e descarrega
                slides, enunciados, exercícios e exames. Coloca cada ficheiro
                na pasta correspondente.
              </p>
            </StepCard>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-muted-200 bg-muted-50 p-8 text-center dark:border-muted-800 dark:bg-muted-900">
          <p className="text-lg font-semibold text-muted-900">
            Tudo pronto?
          </p>
          <p className="mt-2 text-sm text-muted-500">
            Envia-me uma mensagem para confirmarmos a primeira sessão.
          </p>
          <a
            href="https://wa.me/351925194895"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-8 py-3 font-semibold text-white shadow-md transition-all hover:bg-brand/90 hover:shadow-lg"
          >
            <Icon name="chat-bubble" className="h-5 w-5" />
            Enviar mensagem no WhatsApp
          </a>
        </div>

      </div>
    </Container>
  );
};
export default Route;
